import React, { useCallback, useEffect, useState } from "react";
import { API_URL } from "../../utils/config";
import { formatNumero } from "../../utils/Utilitats";
import { useUserContext } from "../../utils/UserContext";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import AjudaTreball from "./AjudaTreball";
import { BotoAjuda } from "./AjudaModal";
import TeclaEscEnter from "../../utils/Teclaescenter";
import "../dashboard_v2.css";

const ESTATS = {
  PENDENT_APROVISIONAR: "Pendent d'aprovisionar",
  PENDENT_RECEPCIO: "Pendent de recepció",
  PENDENT_RESERVA: "Pendent de reserva",
  PENDENT_TRASLLAT: "Pendent de trasllat",
  COMPLET: "Complet",
};

const FILTRES = [
  { id: "tots", label: "Tots" },
  { id: "pendents", label: "Pendents" },
  { id: "proveidor", label: "Proveïdor" },
  { id: "trasllat", label: "Per traslladar" },
  { id: "obra", label: "A obra" },
];

const GRUPS_UBICACIO = [
  ["magatzems", "Magatzems"],
  ["vehicles", "Vehicles"],
  ["obres", "Obres"],
  ["altres", "Altres"],
];

function etiquetaEstat(estat) {
  return ESTATS[estat] || estat || "—";
}

function numero(valor) {
  return Number(String(valor ?? "").replace(",", "."));
}

function passaFiltre(fila, filtre) {
  if (filtre === "pendents") return fila.estat !== "COMPLET";
  if (filtre === "proveidor") return Number(fila.proveidor) > 0.001 || Number(fila.pendentRecepcio) > 0.001;
  if (filtre === "trasllat") return Number(fila.pendentTrasllat) > 0.001;
  if (filtre === "obra") return !!fila.hiHaObra || Number(fila.aObra) > 0.001;
  return true;
}

function nomTipus(tipus) {
  if (tipus === "pack") return "Pack";
  if (tipus === "material") return "Material";
  return "Producte";
}

async function cridar(url, cos) {
  const resposta = await fetchAutenticat(`${API_URL}/${url}`, {
    method: "POST",
    body: JSON.stringify(cos),
  });
  const dades = await resposta.json();
  if (!dades.success) {
    throw new Error(dades.message || "No s'ha pogut completar l'acció.");
  }
  return dades;
}

function LogisticaTreball({ treball, onCancel, subnav }) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const idEmpresa = empresaSeleccionada?.IdEmpresa;
  const potActuar = Number(rolUsuari) >= 3;
  const [vista, setVista] = useState(null);
  const [error, setError] = useState("");
  const [carregant, setCarregant] = useState(true);
  const [filtre, setFiltre] = useState("tots");
  const [oberta, setOberta] = useState(null);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  TeclaEscEnter(onCancel, null);

  const carregar = useCallback(async () => {
    if (!treball?.idTreballs || !idEmpresa) return;
    setCarregant(true);
    setError("");
    try {
      const dades = await cridar("Logisticatreball.php", {
        idEmpresa,
        idTreballs: treball.idTreballs,
      });
      setVista(dades);
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      setError(e.message || "No s'ha pogut carregar la logística.");
    } finally {
      setCarregant(false);
    }
  }, [treball, idEmpresa]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const files = (vista?.files || []).filter((fila) => passaFiltre(fila, filtre));
  const resum = vista?.resum || {};
  const preparat = Number(resum.necessitats) > 0 &&
    !resum.pendentAprovisionar &&
    !resum.pendentRecepcio &&
    !resum.pendentReserva &&
    !resum.pendentTrasllat;

  return (
    <div className="dv2-entity">
      {mostraAjuda && <AjudaTreball pestanya="logisticaTreball" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className="dv2-pageHeader-icon" aria-hidden="true">📦</div>
          <div>
            <h1 className="dv2-pageHeader-title">Logística</h1>
            <p className="dv2-pageHeader-subtitle">
              {treball?.idTreballIntern || treball?.idTreballs}
              {treball?.NomClient ? ` · ${treball.NomClient}` : ""}
            </p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funciona la logística del treball" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>Tornar</button>
        </div>
      </div>
      {subnav}
      <div className="dv2-entity-body">
        <p className="dv2-hint">Què ens falta per poder executar aquest treball?</p>
        {vista?.obra?.nom ? (
          <p className="dv2-hint">
            Obra: {vista.obra.nom}
            {vista.obra.activa ? "" : " (inactiva: no es proposa com a destí)"}
          </p>
        ) : (
          <p className="dv2-hint">Aquest treball no té una sola obra activa. El trasllat no proposa destí.</p>
        )}
        {error ? <p className="dv2-banner dv2-banner--danger">{error}</p> : null}
        {carregant && !vista ? (
          <p className="dv2-hint">Carregant…</p>
        ) : (
          <div className="dv2-fields dv2-fields--sizes">
            <Camp etiqueta="Necessitats" valor={resum.necessitats ?? 0} />
            <Camp etiqueta="Completes" valor={resum.completes ?? 0} />
            <Camp etiqueta="Pendent d'aprovisionar" valor={resum.pendentAprovisionar ?? 0} />
            <Camp etiqueta="Pendent de recepció" valor={resum.pendentRecepcio ?? 0} />
            <Camp etiqueta="Pendent de reserva" valor={resum.pendentReserva ?? 0} />
            <Camp etiqueta="Pendent de trasllat" valor={resum.pendentTrasllat ?? 0} />
          </div>
        )}
        {preparat ? <p className="dv2-hint">Preparat: cap necessitat queda pendent.</p> : null}

        <div className="dv2-filterBar">
          {FILTRES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`dv2-btn ${filtre === item.id ? "dv2-btn--primary" : ""}`}
              onClick={() => setFiltre(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {!carregant && files.length === 0 ? (
          <p className="dv2-empty">
            {vista?.files?.length ? "Cap necessitat coincideix amb el filtre." : "Aquest treball no té necessitats de material."}
          </p>
        ) : (
          <div className="dv2-tableWrap">
            <table className="dv2-table">
              <thead>
                <tr>
                  <th>Necessitat</th>
                  <th className="dv2-num">Necessari</th>
                  <th>Estat</th>
                  <th>Ubicació</th>
                  <th className="dv2-num">A l'obra</th>
                </tr>
              </thead>
              <tbody>
                {files.map((fila) => (
                  <FilaNecessitat
                    key={fila.clau}
                    fila={fila}
                    oberta={oberta === fila.clau}
                    onToggle={() => setOberta(oberta === fila.clau ? null : fila.clau)}
                    idEmpresa={idEmpresa}
                    obra={vista?.obra}
                    potActuar={potActuar}
                    onCanvi={carregar}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Camp({ etiqueta, valor }) {
  return (
    <div className="dv2-field dv2-field--curt">
      <span className="dv2-label">{etiqueta}</span>
      <span className="dv2-value">{valor}</span>
    </div>
  );
}

function FilaNecessitat({ fila, oberta, onToggle, idEmpresa, obra, potActuar, onCanvi }) {
  return (
    <>
      <tr data-clau={fila.clau} onClick={onToggle} style={{ cursor: "pointer" }}>
        <td>
          {fila.nom}
          <div className="dv2-hint">{nomTipus(fila.tipus)}{fila.unitat ? ` · ${fila.unitat}` : ""}</div>
        </td>
        <td className="dv2-num">{formatNumero(fila.necessari)}</td>
        <td>{etiquetaEstat(fila.estat)}</td>
        <td>{fila.ubicacio || "—"}</td>
        <td className="dv2-num">{fila.tipus === "material" ? (fila.hiHaObra ? "Sí" : "—") : formatNumero(fila.aObra)}</td>
      </tr>
      {oberta ? (
        <tr data-clau={`${fila.clau}-detall`}>
          <td colSpan={5}>
            <DetallFila fila={fila} idEmpresa={idEmpresa} obra={obra} potActuar={potActuar} onCanvi={onCanvi} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function DetallFila({ fila, idEmpresa, obra, potActuar, onCanvi }) {
  const [accio, setAccio] = useState(null);
  const producteFila = fila.idProducte
    ? {
        idProducte: fila.idProducte,
        nom: fila.nom,
        distribucio: fila.distribucio || [],
        estocFisic: fila.estocFisic,
        estocReservat: fila.estocReservat,
        estocDisponible: fila.estocDisponible,
      }
    : null;

  return (
    <div>
      <div className="dv2-fields dv2-fields--sizes">
        <Camp etiqueta="Estoc previst" valor={formatNumero(fila.estoc)} />
        <Camp etiqueta="Proveïdor" valor={formatNumero(fila.proveidor)} />
        <Camp etiqueta="Packs" valor={formatNumero(fila.packs)} />
        <Camp etiqueta="Reservat del treball" valor={formatNumero(fila.reservat)} />
        <Camp etiqueta="Rebut" valor={fila.tipus === "pack" ? "al detall" : formatNumero(fila.rebut)} />
        {fila.idProducte ? (
          <>
            <Camp etiqueta="Estoc físic" valor={formatNumero(fila.estocFisic)} />
            <Camp etiqueta="Reservat empresa" valor={formatNumero(fila.estocReservat)} />
            <Camp etiqueta="Disponible" valor={formatNumero(fila.estocDisponible)} />
          </>
        ) : null}
      </div>
      <p className="dv2-hint">
        Pendent d'aprovisionar {formatNumero(fila.pendentAprovisionar)} · recepció {formatNumero(fila.pendentRecepcio)} · reserva {formatNumero(fila.pendentReserva)} · trasllat {formatNumero(fila.pendentTrasllat)}
      </p>
      {fila.tasques?.length ? (
        <p className="dv2-hint">
          Tasques: {fila.tasques.map((tasca) => `${tasca.descripcio || tasca.idTasques} (${formatNumero(tasca.necessari)})`).join(" · ")}
        </p>
      ) : null}
      {fila.distribucio?.length ? <LlistaUbicacions files={fila.distribucio} /> : null}

      {potActuar && producteFila ? (
        <div className="dv2-pageHeader-actions" style={{ margin: "8px 0" }}>
          <button type="button" className="dv2-btn" onClick={() => setAccio({ tipus: "trasllat", producte: producteFila })}>Traslladar</button>
          <button type="button" className="dv2-btn" onClick={() => setAccio({ tipus: "corregir", producte: producteFila })}>Corregir estoc</button>
        </div>
      ) : null}
      {accio?.tipus === "trasllat" ? (
        <FormulariTrasllat producte={accio.producte} idEmpresa={idEmpresa} obra={obra} onTancar={() => setAccio(null)} onFet={onCanvi} />
      ) : null}
      {accio?.tipus === "corregir" ? (
        <FormulariCorregir producte={accio.producte} idEmpresa={idEmpresa} onTancar={() => setAccio(null)} onFet={onCanvi} />
      ) : null}
      {accio?.tipus === "reserva" ? (
        <FormulariReserva linia={accio.linia} idEmpresa={idEmpresa} onTancar={() => setAccio(null)} onFet={onCanvi} />
      ) : null}
      {accio?.tipus === "recepcio" ? (
        <FormulariRecepcio linia={accio.linia} idEmpresa={idEmpresa} obra={obra} onTancar={() => setAccio(null)} onFet={onCanvi} />
      ) : null}

      {fila.components?.length ? <Components files={fila.components} /> : null}
      {fila.packsDetall?.map((pack) => (
        <PackDetall key={pack.idProducte} pack={pack} idEmpresa={idEmpresa} obra={obra} potActuar={potActuar} onAccio={setAccio} />
      ))}
      {fila.productesDirectes?.map((producte) => (
        <div key={producte.idProducte} style={{ marginTop: 8 }}>
          <p className="dv2-hint">{producte.nom} · ubicació {producte.ubicacio || "—"} · a l'obra {formatNumero(producte.aObra)}</p>
          {potActuar ? (
            <div className="dv2-pageHeader-actions">
              <button type="button" className="dv2-btn" onClick={() => setAccio({ tipus: "trasllat", producte })}>Traslladar</button>
              <button type="button" className="dv2-btn" onClick={() => setAccio({ tipus: "corregir", producte })}>Corregir estoc</button>
            </div>
          ) : null}
        </div>
      ))}
      {fila.linies?.length ? (
        <Linies files={fila.linies} potActuar={potActuar} onAccio={setAccio} idEmpresa={idEmpresa} onFet={onCanvi} />
      ) : (
        <p className="dv2-hint">Encara no hi ha línies d'aprovisionament.</p>
      )}
    </div>
  );
}

function LlistaUbicacions({ files }) {
  const positives = files.filter((fila) => Number(fila.quantitat) > 0);
  if (!positives.length) return null;
  return (
    <p className="dv2-hint">
      {positives.map((fila) => `${fila.nom} (${fila.tipus}) ${formatNumero(fila.quantitat)}`).join(" · ")}
    </p>
  );
}

function Components({ files }) {
  return (
    <div className="dv2-tableWrap">
      <table className="dv2-table">
        <thead>
          <tr>
            <th>Component</th>
            <th className="dv2-num">Necessari</th>
            <th className="dv2-num">Estoc</th>
            <th className="dv2-num">Proveïdor</th>
            <th className="dv2-num">Rebut</th>
            <th className="dv2-num">Reservat</th>
            <th className="dv2-num">A l'obra</th>
            <th>Ubicació</th>
          </tr>
        </thead>
        <tbody>
          {files.map((component) => (
            <tr key={component.idProducte}>
              <td>{component.nom}</td>
              <td className="dv2-num">{formatNumero(component.necessari)}</td>
              <td className="dv2-num">{formatNumero(component.estoc)}</td>
              <td className="dv2-num">{formatNumero(component.proveidor)}</td>
              <td className="dv2-num">{formatNumero(component.rebut)}</td>
              <td className="dv2-num">{formatNumero(component.reservat)}</td>
              <td className="dv2-num">{formatNumero(component.aObra)}</td>
              <td>{component.ubicacio || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PackDetall({ pack, potActuar, onAccio }) {
  const producte = {
    idProducte: pack.idProducte,
    nom: pack.nom,
    distribucio: pack.distribucio || [],
    estocFisic: pack.estocFisic,
    estocReservat: pack.estocReservat,
    estocDisponible: pack.estocDisponible,
  };
  return (
    <div style={{ marginTop: 8 }}>
      <p className="dv2-hint">
        Pack {pack.nom}: {formatNumero(pack.quantitat)} · {etiquetaEstat(pack.estat)} · reservat {formatNumero(pack.reservat)} · a l'obra {formatNumero(pack.aObra)}
      </p>
      {potActuar ? (
        <div className="dv2-pageHeader-actions">
          <button type="button" className="dv2-btn" onClick={() => onAccio({ tipus: "trasllat", producte })}>Traslladar</button>
          <button type="button" className="dv2-btn" onClick={() => onAccio({ tipus: "corregir", producte })}>Corregir estoc</button>
        </div>
      ) : null}
      {pack.components?.length ? <Components files={pack.components} /> : null}
    </div>
  );
}

function Linies({ files, potActuar, onAccio, idEmpresa, onFet }) {
  return (
    <div className="dv2-tableWrap">
      <table className="dv2-table">
        <thead>
          <tr>
            <th>Línia</th>
            <th>Origen</th>
            <th className="dv2-num">Quantitat</th>
            <th className="dv2-num">Reservat</th>
            <th className="dv2-num">Rebut</th>
            <th className="dv2-table-actions">Accions</th>
          </tr>
        </thead>
        <tbody>
          {files.map((linia) => (
            <tr key={linia.idAprovisionamentTasques}>
              <td>
                {linia.nom}
                {linia.idAprovisionamentPacks ? <div className="dv2-hint">Component de pack</div> : null}
              </td>
              <td>{linia.origen === "ESTOC" ? "Estoc" : "Proveïdor"}</td>
              <td className="dv2-num">{formatNumero(linia.quantitat)}</td>
              <td className="dv2-num">{formatNumero(linia.reservat)}</td>
              <td className="dv2-num">{formatNumero(linia.rebut)}</td>
              <td className="dv2-table-actions">
                {potActuar && linia.origen === "ESTOC" && Number(linia.pendentReserva) > 0.001 ? (
                  <button type="button" className="dv2-btn" onClick={() => onAccio({ tipus: "reserva", linia })}>Reservar</button>
                ) : null}
                {potActuar && linia.origen === "PROVEIDOR" && Number(linia.pendentRebre) > 0.001 ? (
                  <button type="button" className="dv2-btn" onClick={() => onAccio({ tipus: "recepcio", linia })}>Rebre</button>
                ) : null}
                {potActuar && (linia.reserves || []).map((reserva) => (
                  <button
                    key={reserva.idReservesEstoc}
                    type="button"
                    className="dv2-btn"
                    onClick={() => alliberar(reserva, idEmpresa, onFet)}
                  >
                    Alliberar {formatNumero(reserva.quantitat)}
                  </button>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function alliberar(reserva, idEmpresa, onFet) {
  if (!window.confirm("Vols alliberar aquesta reserva? Deixarà de comprometre estoc. Si ve d'una recepció, el material rebut es queda a l'estoc.")) return;
  try {
    await cridar("Alliberarreservaestoc.php", { idEmpresa, idReservesEstoc: reserva.idReservesEstoc });
    onFet();
  } catch (e) {
    if (e instanceof SessioCaducadaError) return;
    alert(e.message || "No s'ha pogut alliberar la reserva.");
  }
}

function FormulariReserva({ linia, idEmpresa, onTancar, onFet }) {
  const [quantitat, setQuantitat] = useState(String(linia.pendentReserva || ""));
  const [ocupat, setOcupat] = useState(false);
  const desar = async (event) => {
    event.preventDefault();
    const q = numero(quantitat);
    if (!Number.isFinite(q) || q <= 0) {
      alert("La quantitat ha de ser superior a zero.");
      return;
    }
    setOcupat(true);
    try {
      await cridar("Novareservaestoc.php", {
        idEmpresa,
        idAprovisionamentTasques: linia.idAprovisionamentTasques,
        quantitat: q,
      });
      onTancar();
      onFet();
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert(e.message || "No s'ha pogut reservar.");
    } finally {
      setOcupat(false);
    }
  };
  return (
    <form className="dv2-fields" onSubmit={desar}>
      <label className="dv2-field dv2-field--curt">
        <span className="dv2-label">Reservar {linia.nom}</span>
        <input className="dv2-input" value={quantitat} onChange={(e) => setQuantitat(e.target.value)} />
        <span className="dv2-hint">Pendent de reserva de la línia: {formatNumero(linia.pendentReserva)}</span>
      </label>
      <Botons ocupat={ocupat} text="Reservar" onTancar={onTancar} />
    </form>
  );
}

function FormulariRecepcio({ linia, idEmpresa, obra, onTancar, onFet }) {
  const [quantitat, setQuantitat] = useState(String(linia.pendentRebre || ""));
  const [idUbicacio, setIdUbicacio] = useState(obra?.activa ? String(obra.idUbicacions) : "");
  const [ubicacions, setUbicacions] = useState(null);
  const [ocupat, setOcupat] = useState(false);

  useEffect(() => {
    let viu = true;
    fetchAutenticat(`${API_URL}/Ubicacions.php`, {
      method: "POST",
      body: JSON.stringify({ idEmpresa }),
    })
      .then((resposta) => resposta.json())
      .then((dades) => {
        if (viu && dades.success) setUbicacions(dades);
      })
      .catch((e) => {
        if (e instanceof SessioCaducadaError) return;
      });
    return () => {
      viu = false;
    };
  }, [idEmpresa]);

  const desar = async (event) => {
    event.preventDefault();
    const q = numero(quantitat);
    if (!Number.isFinite(q) || q <= 0) {
      alert("La quantitat ha de ser superior a zero.");
      return;
    }
    if (!idUbicacio) {
      alert("Cal una ubicació.");
      return;
    }
    setOcupat(true);
    try {
      await cridar("Rebreproducteaprovisionament.php", {
        idEmpresa,
        idAprovisionamentTasques: linia.idAprovisionamentTasques,
        quantitat: q,
        idUbicacions: Number(idUbicacio),
      });
      onTancar();
      onFet();
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert(e.message || "No s'ha pogut rebre.");
    } finally {
      setOcupat(false);
    }
  };

  return (
    <form className="dv2-fields" onSubmit={desar}>
      <label className="dv2-field dv2-field--curt">
        <span className="dv2-label">Quantitat rebuda</span>
        <input className="dv2-input" value={quantitat} onChange={(e) => setQuantitat(e.target.value)} />
        <span className="dv2-hint">Pendent: {formatNumero(linia.pendentRebre)}. Una ubicació Altre nova es crea des de la logística de la tasca.</span>
      </label>
      <SelectUbicacions idUbicacio={idUbicacio} onChange={setIdUbicacio} ubicacions={ubicacions} />
      <Botons ocupat={ocupat} text="Rebre" onTancar={onTancar} />
    </form>
  );
}

function FormulariTrasllat({ producte, idEmpresa, obra, onTancar, onFet }) {
  const origens = (producte.distribucio || []).filter((fila) => Number(fila.quantitat) > 0);
  const [idOrigen, setIdOrigen] = useState(origens[0] ? String(origens[0].idUbicacions) : "");
  const [idDesti, setIdDesti] = useState(obra?.activa ? String(obra.idUbicacions) : "");
  const [quantitat, setQuantitat] = useState("");
  const [observacions, setObservacions] = useState("");
  const [destinacions, setDestinacions] = useState(null);
  const [ocupat, setOcupat] = useState(false);
  const origen = origens.find((fila) => String(fila.idUbicacions) === String(idOrigen));

  useEffect(() => {
    let viu = true;
    fetchAutenticat(`${API_URL}/Ubicacions.php`, {
      method: "POST",
      body: JSON.stringify({ idEmpresa }),
    })
      .then((resposta) => resposta.json())
      .then((dades) => {
        if (viu && dades.success) setDestinacions(dades);
      })
      .catch((e) => {
        if (e instanceof SessioCaducadaError) return;
      });
    return () => {
      viu = false;
    };
  }, [idEmpresa]);

  const desar = async (event) => {
    event.preventDefault();
    const q = numero(quantitat);
    if (!origen) {
      alert("Cal una ubicació d'origen.");
      return;
    }
    if (!Number.isFinite(q) || q <= 0) {
      alert("La quantitat ha de ser superior a zero.");
      return;
    }
    if (q > Number(origen.quantitat) + 0.001) {
      alert(`El màxim que es pot traslladar des d'aquí és ${formatNumero(origen.quantitat)}.`);
      return;
    }
    if (!idDesti || String(idDesti) === String(idOrigen)) {
      alert("Cal un destí diferent de l'origen.");
      return;
    }
    setOcupat(true);
    try {
      await cridar("Traslladarproducte.php", {
        idEmpresa,
        idProductes: producte.idProducte,
        quantitat: q,
        idUbicacioOrigen: Number(idOrigen),
        idUbicacioDesti: Number(idDesti),
        observacions: observacions.trim() || null,
      });
      onTancar();
      onFet();
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert(e.message || "No s'ha pogut fer el trasllat.");
    } finally {
      setOcupat(false);
    }
  };

  return (
    <form className="dv2-fields" onSubmit={desar}>
      <p className="dv2-hint">Traslladar {producte.nom}. No canvia l'estoc total ni les reserves.</p>
      <label className="dv2-field">
        <span className="dv2-label">Origen</span>
        <select className="dv2-select" value={idOrigen} onChange={(e) => setIdOrigen(e.target.value)}>
          <option value="">Tria un origen</option>
          {origens.map((fila) => (
            <option key={fila.idUbicacions} value={fila.idUbicacions}>
              {fila.nom} · {formatNumero(fila.quantitat)}
            </option>
          ))}
        </select>
      </label>
      <label className="dv2-field dv2-field--curt">
        <span className="dv2-label">Quantitat</span>
        <input className="dv2-input" value={quantitat} onChange={(e) => setQuantitat(e.target.value)} />
      </label>
      <SelectUbicacions idUbicacio={idDesti} onChange={setIdDesti} ubicacions={destinacions} etiqueta="Destí" />
      <label className="dv2-field dv2-field--llarg">
        <span className="dv2-label">Observacions</span>
        <input className="dv2-input" value={observacions} onChange={(e) => setObservacions(e.target.value)} />
      </label>
      <Botons ocupat={ocupat} text="Traslladar" onTancar={onTancar} />
    </form>
  );
}

function FormulariCorregir({ producte, idEmpresa, onTancar, onFet }) {
  const [signe, setSigne] = useState("positiva");
  const [quantitat, setQuantitat] = useState("");
  const [idUbicacio, setIdUbicacio] = useState("");
  const [motiu, setMotiu] = useState("");
  const [ubicacions, setUbicacions] = useState(null);
  const [ocupat, setOcupat] = useState(false);
  const origens = (producte.distribucio || []).filter((fila) => Number(fila.quantitat) > 0);

  useEffect(() => {
    let viu = true;
    fetchAutenticat(`${API_URL}/Ubicacions.php`, {
      method: "POST",
      body: JSON.stringify({ idEmpresa }),
    })
      .then((resposta) => resposta.json())
      .then((dades) => {
        if (viu && dades.success) setUbicacions(dades);
      })
      .catch((e) => {
        if (e instanceof SessioCaducadaError) return;
      });
    return () => {
      viu = false;
    };
  }, [idEmpresa]);

  const desar = async (event) => {
    event.preventDefault();
    const q = numero(quantitat);
    if (!Number.isFinite(q) || q <= 0) {
      alert("La quantitat ha de ser superior a zero.");
      return;
    }
    if (!motiu.trim()) {
      alert("El motiu és obligatori.");
      return;
    }
    if (!idUbicacio) {
      alert("Cal una ubicació.");
      return;
    }
    const origen = origens.find((fila) => String(fila.idUbicacions) === String(idUbicacio));
    if (signe === "negativa" && q > (Number(origen?.quantitat) || 0) + 0.001) {
      alert(`El màxim que es pot treure d'aquí és ${formatNumero(origen?.quantitat || 0)}.`);
      return;
    }
    const fisic = Number(producte.estocFisic) || 0;
    const reservat = Number(producte.estocReservat) || 0;
    const nouDisponible = fisic + (signe === "negativa" ? -q : q) - reservat;
    if (nouDisponible < -0.001 && !window.confirm("Aquesta regularització deixarà l'estoc disponible en negatiu. Vols continuar?")) {
      return;
    }
    setOcupat(true);
    try {
      await cridar("Regularitzarproducte.php", {
        idEmpresa,
        idProductes: producte.idProducte,
        quantitat: q,
        signe,
        idUbicacions: Number(idUbicacio),
        observacions: motiu.trim(),
      });
      onTancar();
      onFet();
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert(e.message || "No s'ha pogut fer la regularització.");
    } finally {
      setOcupat(false);
    }
  };

  return (
    <form className="dv2-fields" onSubmit={desar}>
      <p className="dv2-hint">Corregir estoc de {producte.nom}. El motiu queda a l'històric.</p>
      <label className="dv2-field">
        <span className="dv2-label">Signe</span>
        <select className="dv2-select" value={signe} onChange={(e) => { setSigne(e.target.value); setIdUbicacio(""); }}>
          <option value="positiva">Entrada (suma estoc)</option>
          <option value="negativa">Sortida (resta estoc)</option>
        </select>
      </label>
      {signe === "negativa" ? (
        <label className="dv2-field">
          <span className="dv2-label">Ubicació</span>
          <select className="dv2-select" value={idUbicacio} onChange={(e) => setIdUbicacio(e.target.value)}>
            <option value="">Tria una ubicació</option>
            {origens.map((fila) => (
              <option key={fila.idUbicacions} value={fila.idUbicacions}>
                {fila.nom} · {formatNumero(fila.quantitat)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <SelectUbicacions idUbicacio={idUbicacio} onChange={setIdUbicacio} ubicacions={ubicacions} />
      )}
      <label className="dv2-field dv2-field--curt">
        <span className="dv2-label">Quantitat</span>
        <input className="dv2-input" value={quantitat} onChange={(e) => setQuantitat(e.target.value)} />
      </label>
      <label className="dv2-field dv2-field--llarg">
        <span className="dv2-label">Motiu</span>
        <input className="dv2-input" value={motiu} onChange={(e) => setMotiu(e.target.value)} />
      </label>
      <Botons ocupat={ocupat} text="Corregir" onTancar={onTancar} />
    </form>
  );
}

function SelectUbicacions({ idUbicacio, onChange, ubicacions, etiqueta = "Ubicació" }) {
  return (
    <label className="dv2-field dv2-field--llarg">
      <span className="dv2-label">{etiqueta}</span>
      <select className="dv2-select" value={idUbicacio} onChange={(e) => onChange(e.target.value)}>
        <option value="">Tria una ubicació</option>
        {GRUPS_UBICACIO.map(([clau, etiq]) => {
          const items = ubicacions?.[clau] || [];
          if (!items.length) return null;
          return (
            <optgroup key={clau} label={etiq}>
              {items.map((item) => (
                <option key={item.idUbicacions} value={item.idUbicacions}>
                  {item.nom}
                  {item.adreca ? ` · ${item.adreca}` : ""}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>
    </label>
  );
}

function Botons({ ocupat, text, onTancar }) {
  return (
    <div className="dv2-pageHeader-actions">
      <button type="submit" className="dv2-btn dv2-btn--primary" disabled={ocupat}>{ocupat ? "Desant…" : text}</button>
      <button type="button" className="dv2-btn" onClick={onTancar} disabled={ocupat}>Cancel·lar</button>
    </div>
  );
}

export function ResumLogisticaTasca({ tasca, idTreball, onAprovisionar, onVeureTreball }) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const idEmpresa = empresaSeleccionada?.IdEmpresa;
  const potActuar = Number(rolUsuari) >= 3;
  const [vista, setVista] = useState(null);
  const [error, setError] = useState("");
  const [accio, setAccio] = useState(null);

  const carregar = useCallback(async () => {
    const idTreballs = tasca?.Treballs_idTreballs || idTreball;
    if (Number(rolUsuari) < 3 || !tasca?.idTasques || !idTreballs || !idEmpresa) return;
    try {
      const dades = await cridar("Logisticatreball.php", {
        idEmpresa,
        idTreballs,
        idTasques: tasca.idTasques,
      });
      setVista(dades);
      setError("");
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      setError(e.message || "No s'ha pogut carregar el resum.");
    }
  }, [tasca, idTreball, idEmpresa, rolUsuari]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (Number(rolUsuari) < 3) return null;
  const fila = vista?.files?.[0];
  const reservables = (fila?.linies || []).filter((linia) => linia.origen === "ESTOC" && Number(linia.pendentReserva) > 0.001);
  const rebudes = (fila?.linies || []).filter((linia) => linia.origen === "PROVEIDOR" && Number(linia.pendentRebre) > 0.001);
  const producte = fila?.idProducte && fila.tipus !== "material"
    ? {
        idProducte: fila.idProducte,
        nom: fila.nom,
        distribucio: fila.distribucio || [],
        estocFisic: fila.estocFisic,
        estocReservat: fila.estocReservat,
        estocDisponible: fila.estocDisponible,
      }
    : null;

  return (
    <section className="dv2-card" style={{ margin: "12px 16px 0" }}>
      <div className="dv2-card-head">
        <h2 className="dv2-card-title">Logística d'aquesta tasca</h2>
      </div>
      {error ? <p className="dv2-banner dv2-banner--danger">{error}</p> : null}
      {!fila ? (
        <p className="dv2-hint">{vista ? "Aquesta tasca no té necessitat de material pendent de mostrar." : "Carregant…"}</p>
      ) : (
        <>
          <div className="dv2-fields dv2-fields--sizes">
            <Camp etiqueta="Necessitat" valor={fila.nom} />
            <Camp etiqueta="Necessari" valor={formatNumero(fila.necessari)} />
            <Camp etiqueta="Reservat" valor={formatNumero(fila.reservat)} />
            <Camp etiqueta="Rebut" valor={fila.tipus === "pack" ? "al detall" : formatNumero(fila.rebut)} />
            <Camp etiqueta="Ubicació" valor={fila.ubicacio || "—"} />
            <Camp etiqueta="Estat" valor={etiquetaEstat(fila.estat)} />
          </div>
          <p className="dv2-hint">
            Pendent d'aprovisionar {formatNumero(fila.pendentAprovisionar)} · recepció {formatNumero(fila.pendentRecepcio)} · reserva {formatNumero(fila.pendentReserva)} · trasllat {formatNumero(fila.pendentTrasllat)}
          </p>
        </>
      )}
      <div className="dv2-pageHeader-actions" style={{ marginTop: 8 }}>
        <button type="button" className="dv2-btn dv2-btn--primary" onClick={onAprovisionar}>Gestionar aprovisionament</button>
        {onVeureTreball ? (
          <button type="button" className="dv2-btn" onClick={onVeureTreball}>Veure logística del treball</button>
        ) : null}
        {potActuar && reservables.length === 1 ? (
          <button type="button" className="dv2-btn" onClick={() => setAccio({ tipus: "reserva", linia: reservables[0] })}>Reservar</button>
        ) : null}
        {potActuar && rebudes.length === 1 ? (
          <button type="button" className="dv2-btn" onClick={() => setAccio({ tipus: "recepcio", linia: rebudes[0] })}>Rebre</button>
        ) : null}
        {potActuar && producte ? (
          <>
            <button type="button" className="dv2-btn" onClick={() => setAccio({ tipus: "trasllat", producte })}>Traslladar</button>
            <button type="button" className="dv2-btn" onClick={() => setAccio({ tipus: "corregir", producte })}>Corregir estoc</button>
          </>
        ) : null}
      </div>
      {accio?.tipus === "reserva" ? (
        <FormulariReserva linia={accio.linia} idEmpresa={idEmpresa} onTancar={() => setAccio(null)} onFet={carregar} />
      ) : null}
      {accio?.tipus === "recepcio" ? (
        <FormulariRecepcio linia={accio.linia} idEmpresa={idEmpresa} obra={vista?.obra} onTancar={() => setAccio(null)} onFet={carregar} />
      ) : null}
      {accio?.tipus === "trasllat" ? (
        <FormulariTrasllat producte={accio.producte} idEmpresa={idEmpresa} obra={vista?.obra} onTancar={() => setAccio(null)} onFet={carregar} />
      ) : null}
      {accio?.tipus === "corregir" ? (
        <FormulariCorregir producte={accio.producte} idEmpresa={idEmpresa} onTancar={() => setAccio(null)} onFet={carregar} />
      ) : null}
    </section>
  );
}

export default LogisticaTreball;
